import type { PopUpNotificationProperties } from 'dynamo-daos/build/models/misc/userNotificationProperties';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';

import type { UserNotification } from '@cartwhl/common/types';
import { resolveNotificationLinkUrl } from '@cartwhl/common/utils/resolveNotificationLinkUrl';
import Text from '@cartwhl/ui/base/TextNew';

import './UserMenuPopUp.css';

const POINTER_WIDTH = 38;
const POINTER_HEIGHT = 30;

export const USER_MENU_POP_UP_POINTER_OFFSET_PX = POINTER_HEIGHT;

interface UserMenuPopUpProps {
  notification: UserNotification;
  onDismiss: () => void;
  onLinkActivate: () => void | Promise<void>;
  /** Distance from the anchor's right; tip sits on the avatar center. */
  pointerTipRightPx: number;
  /** Distance from the anchor top to the pop-up container top (positions tip at avatar bottom). */
  popUpTopPx: number;
}

function PopUpPointer({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={POINTER_WIDTH}
      height={POINTER_HEIGHT}
      viewBox={`0 0 ${POINTER_WIDTH} ${POINTER_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden
    >
      <path
        d={`M0 ${POINTER_HEIGHT}C11 ${POINTER_HEIGHT} 29 26 ${POINTER_WIDTH} 0V${POINTER_HEIGHT}H0Z`}
        className="fill-green"
      />
    </svg>
  );
}

function PopUpLink({
  children,
  href,
  onActivate,
}: {
  children: ReactNode;
  href: string;
  onActivate: () => void | Promise<void>;
}) {
  const url = new URL(href, window.location.origin);

  const className =
    'bg-primary text-primary outline-hidden flex w-full items-center justify-center rounded-full px-4 py-2.5 transition-opacity hover:opacity-90';

  const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    try {
      await onActivate();
    } finally {
      window.location.assign(url.href);
    }
  };

  return (
    <a href={url.href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

export default function UserMenuPopUp({
  notification,
  onDismiss,
  onLinkActivate,
  pointerTipRightPx,
  popUpTopPx,
}: UserMenuPopUpProps) {
  const { link, linkText, subtitle, title } =
    notification.properties as unknown as PopUpNotificationProperties;

  const href = link ? resolveNotificationLinkUrl(link) : undefined;

  return (
    <div
      className="user-menu-pop-up-enter pointer-events-auto absolute z-[70] w-[min(100vw-2rem,252px)]"
      style={{ top: popUpTopPx, right: pointerTipRightPx }}
      role="dialog"
      aria-labelledby="user-menu-popup-title"
      aria-describedby="user-menu-popup-subtitle"
    >
      <PopUpPointer
        className="absolute right-0 drop-shadow-md"
        style={{ top: -POINTER_HEIGHT }}
      />
      <div className="bg-green relative flex flex-col gap-3 rounded-b-3xl rounded-tl-3xl px-5 pb-4 pt-6 shadow-lg">
        <div className="flex flex-col gap-1">
          <Text
            id="user-menu-popup-title"
            token="display/20/medium"
            className="pb-3 text-white"
            leading="headline"
          >
            {title ?? ''}
          </Text>
          <Text
            id="user-menu-popup-subtitle"
            token="14"
            className="text-pretty pb-4 text-white/90"
          >
            {subtitle ?? ''}
          </Text>
        </div>
        {href && linkText ? (
          <PopUpLink href={href} onActivate={onLinkActivate}>
            <Text token="14/medium">{linkText}</Text>
          </PopUpLink>
        ) : null}
        <button
          type="button"
          className="outline-hidden flex w-full cursor-pointer justify-center border-0 bg-transparent py-1"
          onClick={onDismiss}
        >
          <Text token="14/medium" className="text-white">
            Dismiss
          </Text>
        </button>
      </div>
    </div>
  );
}
